"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Mic, MicOff } from "lucide-react";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import { cn } from "@/shared/lib/utils";
import type { LfgRank, LfgRole } from "../model/board-filters";

/** Ключ RoleIcon для роли бэкенда: у «Автофилла» — иконка позиции fill. */
const ROLE_ICON_KEY: Record<LfgRole, string> = {
  Top: "top", Jungle: "jungle", Mid: "mid", Adc: "adc", Support: "support",
  Autofill: "fill", Substitute: "substitute", Coach: "coach",
};

/** Чип карточки. `size="sm"` — требования и состав, `md` — шапка. */
export function CardChip({ children, size = "sm", className }: { children: ReactNode; size?: "sm" | "md"; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md font-semibold text-foreground/90",
        size === "md" ? "h-[26px] border border-border bg-muted/40 px-2.5 text-xs" : "h-[22px] bg-muted/60 px-2 text-[11px]",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function RankEmblem({ rank, size = 16 }: { rank: LfgRank; size?: number }) {
  const src = getRiotRankImageUrl(rank);
  return src ? <Image src={src} alt="" width={size} height={size} style={{ width: size, height: size }} /> : null;
}

export function RankLabel({ rank, size = 16 }: { rank: LfgRank; size?: number }) {
  const t = useTranslations("LfgBoard");
  return (
    <>
      <RankEmblem rank={rank} size={size} />
      <span className="truncate">{t(`rank.${rank}`)}</span>
    </>
  );
}

export function LfgRoleIcon({ role, size = 14 }: { role: LfgRole; size?: number }) {
  return <RoleIcon role={ROLE_ICON_KEY[role]} size={size} className="text-current opacity-80" />;
}

/** Микро только иконкой: зелёный — есть, красный — нет; подпись — в title и для скринридера. */
export function MicIcon({ mic, title }: { mic: "With" | "Without"; title: string }) {
  const Icon = mic === "With" ? Mic : MicOff;
  return (
    <span title={title} className="inline-flex">
      <Icon aria-hidden className={cn("size-4", mic === "With" ? "text-score-win" : "text-destructive")} />
      <span className="sr-only">{title}</span>
    </span>
  );
}
